// Local Storage Management for Survey Data
class SurveyStorage {
    constructor() {
        this.storageKey = 'communication_survey_data';
        this.completionKey = 'communication_survey_completed';
        this.currentStepKey = 'communication_survey_current_step';
    }

    // Save form data to localStorage
    saveData(data) {
        try {
            const existingData = this.getData();
            const updatedData = { ...existingData, ...data };
            localStorage.setItem(this.storageKey, JSON.stringify(updatedData));
            return true;
        } catch (error) {
            console.error('Error saving survey data:', error);
            return false;
        }
    }

    // Get form data from localStorage
    getData() {
        try {
            const data = localStorage.getItem(this.storageKey);
            return data ? JSON.parse(data) : {};
        } catch (error) {
            console.error('Error retrieving survey data:', error);
            return {};
        }
    }

    // Save current step
    saveCurrentStep(step) {
        try {
            localStorage.setItem(this.currentStepKey, step.toString());
        } catch (error) {
            console.error('Error saving current step:', error);
        }
    }

    // Get current step
    getCurrentStep() {
        try {
            const step = localStorage.getItem(this.currentStepKey);
            return step ? parseInt(step, 10) : 1;
        } catch (error) {
            console.error('Error retrieving current step:', error);
            return 1;
        }
    }

    // Mark survey as completed
    markCompleted() {
        try {
            const completionData = {
                completed: true,
                timestamp: new Date().toISOString(),
                tkt: this.getTktFromUrl()
            };
            localStorage.setItem(this.completionKey, JSON.stringify(completionData));
            return true;
        } catch (error) {
            console.error('Error marking survey as completed:', error);
            return false;
        }
    }

    // Check if survey is completed
    isCompleted() {
        try {
            const completion = localStorage.getItem(this.completionKey);
            if (completion) {
                const data = JSON.parse(completion);
                return data.completed === true;
            }
            return false;
        } catch (error) {
            console.error('Error checking completion status:', error);
            return false;
        }
    }

    // Get TKT parameter from URL
    getTktFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('tkt') || "";
    }

    // Get CHK parameter from URL
    getChkFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('chk') || "";
    }

    // Clear all survey data
    clearData() {
        try {
            localStorage.removeItem(this.storageKey);
            localStorage.removeItem(this.completionKey);
            localStorage.removeItem(this.currentStepKey);
            return true;
        } catch (error) {
            console.error('Error clearing survey data:', error);
            return false;
        }
    }

    // Get all form data formatted for submission
    getFormattedData() {
        const data = this.getData();
        data["tkt"] = this.getTktFromUrl();
        data["chk"] = this.getChkFromUrl();
        data["timestamp"] = new Date().toISOString();
        return data;
    }

    // Get individual field data
    getField(fieldName) {
        const data = this.getData();
        return data[fieldName] || null;
    }

    // Save individual field data
    saveField(fieldName, value) {
        try {
            const data = this.getData();
            data[fieldName] = value;
            localStorage.setItem(this.storageKey, JSON.stringify(data));
            return true;
        } catch (error) {
            console.error('Error saving field data:', error);
            return false;
        }
    }

    // Restore form state from localStorage
    restoreFormState() {
        const data = this.getData();
        
        // Restore radio button selections
        Object.keys(data).forEach(fieldName => {
            const value = data[fieldName];
            const input = document.querySelector(`input[name="${fieldName}"][value="${value}"]`);
            if (input && input.type === 'radio') {
                input.checked = true;
                // Trigger change event to update UI
                input.dispatchEvent(new Event('change'));
            }
        });

        // Restore textarea content
        const textarea = document.getElementById('openFeedback');
        if (textarea && data.open_feedback) {
            textarea.value = data.open_feedback;
            // Trigger input event to update UI
            textarea.dispatchEvent(new Event('input'));
        }
    }

    // Auto-save form data on input changes
    setupAutoSave() {
        // Save radio button changes
        document.addEventListener('change', (e) => {
            if (e.target.type === 'radio') {
                this.saveField(e.target.name, e.target.value);
            }
        });

        // Save textarea changes with debouncing
        let textareaTimeout;
        document.addEventListener('input', (e) => {
            if (e.target.tagName === 'TEXTAREA') {
                clearTimeout(textareaTimeout);
                textareaTimeout = setTimeout(() => {
                    this.saveField(e.target.name, e.target.value);
                }, 500); // Save after 500ms of no typing
            }
        });

        // Save current step on navigation
        window.addEventListener('beforeunload', () => {
            const currentStep = document.querySelector('.step.active');
            if (currentStep) {
                const stepNumber = parseInt(currentStep.id.replace('step', ''));
                this.saveCurrentStep(stepNumber);
            }
        });
    }
}

// Create global instance
window.surveyStorage = new SurveyStorage();
